// The server's rows, and how they become the website's model.

import type { CustomCategoryInput } from "../domain/categories.ts";
import type { AccountStatus, Rate, SavedPlace, Trip, Vehicle } from "../domain/model.ts";
import { type RateEntry, unitFrom } from "../domain/rates.ts";

/** Every trip column but the route, which only the open trip needs. */
export const tripListColumns = [
  "id", "vehicle_id", "start_date", "end_date", "distance_m", "start_lat", "start_lon", "end_lat", "end_lon",
  "start_place_name", "end_place_name", "is_automatic", "is_entered_by_hand", "is_round_trip", "category",
  "classification_source", "purpose", "notes", "updated_at",
].join(",");

export interface TripRow {
  id: string;
  vehicle_id: string | null;
  start_date: string;
  end_date: string | null;
  distance_m: number;
  start_lat: number | null;
  start_lon: number | null;
  end_lat: number | null;
  end_lon: number | null;
  start_place_name: string | null;
  end_place_name: string | null;
  is_automatic: boolean;
  is_entered_by_hand: boolean;
  is_round_trip: boolean;
  category: string | null;
  classification_source: string | null;
  purpose: string | null;
  notes: string | null;
  updated_at: string;
  route?: number[][] | null;
}

export interface VehicleRow {
  id: string;
  name: string;
  unit: string;
  created_at: string;
  updated_at: string;
}

export interface CustomCategoryRow {
  id: string;
  name: string;
  system_image: string;
  color: string;
  is_deductible: boolean;
  is_archived: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface RateRow {
  id: string;
  year: number;
  start_month: number;
  start_day: number;
  category: string;
  amount: number | string | null;
  unit: string;
  currency_code: string;
  rate_updated_at: string;
  updated_at: string;
}

export interface PlaceRow {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  kind: string;
  updated_at: string;
}

export interface AccountStatusRow {
  subscribed_until: string | null;
  can_upload: boolean;
  data_deleted_after: string | null;
}

function date(text: string): Date {
  return new Date(text);
}

function coordinate(lat: number | null, lon: number | null): [number, number] | null {
  return lat === null || lon === null ? null : [lat, lon];
}

export function tripFrom(row: TripRow): Trip {
  return {
    id: row.id,
    vehicleId: row.vehicle_id,
    start: date(row.start_date),
    end: row.end_date ? date(row.end_date) : null,
    meters: Number(row.distance_m) || 0,
    startName: row.start_place_name,
    endName: row.end_place_name,
    startCoordinate: coordinate(row.start_lat, row.start_lon),
    endCoordinate: coordinate(row.end_lat, row.end_lon),
    isAutomatic: row.is_automatic,
    isEnteredByHand: row.is_entered_by_hand,
    isRoundTrip: row.is_round_trip,
    categoryRaw: row.category,
    classificationSource: row.classification_source,
    purpose: row.purpose,
    notes: row.notes,
    route: routeFrom(row.route),
    updatedAt: date(row.updated_at),
  };
}

/** Tolerant like the app's RouteCodec: malformed points are dropped. */
export function routeFrom(route: unknown): [number, number, number][] {
  if (!Array.isArray(route)) return [];
  return route.flatMap((point): [number, number, number][] => {
    if (!Array.isArray(point) || point.length < 2) return [];
    const [lat, lon, seconds] = point.map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];
    return [[lat!, lon!, Number.isFinite(seconds) ? seconds! : 0]];
  });
}

export function vehicleFrom(row: VehicleRow): Vehicle {
  return { id: row.id, name: row.name || "My Car", unit: unitFrom(row.unit), createdAt: date(row.created_at) };
}

export function customCategoryFrom(row: CustomCategoryRow): CustomCategoryInput {
  return {
    id: row.id,
    name: row.name,
    systemImage: row.system_image,
    color: row.color,
    isDeductible: row.is_deductible,
    isArchived: row.is_archived,
    sortOrder: row.sort_order,
    createdAt: date(row.created_at),
  };
}

export function rateFrom(row: RateRow): Rate {
  return {
    id: row.id,
    year: row.year,
    start: { month: row.start_month, day: row.start_day },
    category: row.category,
    amount: row.amount === null ? null : Number(row.amount),
    unit: unitFrom(row.unit),
    currencyCode: row.currency_code || "USD",
    updatedAt: date(row.rate_updated_at),
  };
}

export function rateEntry(rate: Rate): RateEntry {
  return { year: rate.year, start: rate.start, category: rate.category, amount: rate.amount, unit: rate.unit, currencyCode: rate.currencyCode, updatedAt: rate.updatedAt };
}

export function placeFrom(row: PlaceRow): SavedPlace {
  return { id: row.id, name: row.name, address: row.address, latitude: row.latitude, longitude: row.longitude, kind: row.kind };
}

export function statusFrom(row: AccountStatusRow | undefined): AccountStatus {
  return {
    subscribedUntil: row?.subscribed_until ? date(row.subscribed_until) : null,
    canUpload: row?.can_upload ?? false,
    dataDeletedAfter: row?.data_deleted_after ? date(row.data_deleted_after) : null,
  };
}
